/**
 * Cached hotel image fetch for order cards — uses existing POST /getImage only.
 */

import axios from "axios";
import { ApiReqBaseUrl } from "../../assets/GlobalVariables";
import { normalizePackageCardImageUrl } from "./packageOrderCardSummary.js";

const isDev = import.meta.env.DEV;

/**
 * Origin that serves Flask static paths (lead images, etc.). In dev with Vite proxy,
 * `/api` hits 8080 but bare `/static/...` does not — relative image URLs must use the backend origin.
 */
function resolveBackendOriginForStaticAssets() {
  const env =
    (typeof import.meta !== "undefined" &&
      import.meta.env?.VITE_BACKEND_ORIGIN &&
      String(import.meta.env.VITE_BACKEND_ORIGIN).trim()) ||
    "";
  if (env) return env.replace(/\/$/, "");

  const base = String(ApiReqBaseUrl || "").trim().replace(/\/$/, "");
  if (base.startsWith("http://") || base.startsWith("https://")) {
    try {
      return new URL(base).origin;
    } catch {
      /* ignore */
    }
  }
  if (base === "/api" || base === "") {
    if (isDev) return "http://localhost:8080";
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "";
}

/**
 * Turn DB/API relative paths into a browser-loadable absolute URL for <img src>.
 * @param {unknown} raw
 * @returns {string | null}
 */
export function absolutizeHotelImageUrlForDisplay(raw) {
  const u = normalizePackageCardImageUrl(raw);
  if (!u) return null;
  const t = u.trim();
  if (t.startsWith("data:")) return t;
  if (/^https?:\/\//i.test(t)) return t;
  if (t.startsWith("//")) {
    if (typeof window !== "undefined" && window.location?.protocol) {
      return `${window.location.protocol}${t}`;
    }
    return `https:${t}`;
  }
  const origin = resolveBackendOriginForStaticAssets();
  if (!origin) return t;
  if (t.startsWith("/")) return `${origin}${t}`;
  return `${origin}/${t}`;
}

/** Positive hotel IDs for /getImage (reject UUIDs / huge numbers). */
export function sanitizeNumericHotelIdForImageApi(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw) && raw > 0 && raw < 1e12) {
    return Math.floor(raw);
  }
  const s = String(raw).trim();
  if (!/^\d+$/.test(s)) return null;
  const n = parseInt(s, 10);
  if (!Number.isFinite(n) || n <= 0 || n >= 1e12) return null;
  return n;
}

const memoryCache = new Map();
/** @type {Map<string, Promise<{ url: string | null, source: string }>>} */
const inflight = new Map();

const TTL_OK_MS = 5 * 60 * 1000;
const TTL_FAIL_MS = 20 * 1000;

/** @param {unknown} raw @returns {string | null} */
export function normalizeHotelNameForLeadImageLookup(raw) {
  if (raw == null) return null;
  const t = String(raw).trim();
  if (t.length < 4 || t === "חבילת נופש") return null;
  return t;
}

function cacheKeyForLeadImageLookup(hid, nameNorm) {
  const idPart = hid != null ? `id:${hid}` : "id:n";
  const nPart = nameNorm ? `n:${nameNorm.toLowerCase()}` : "n:";
  return `leadimg:v3:${idPart}:${nPart}`;
}

/**
 * וריאציות שם ל־leadImageByHotelName — השם המלא מההזמנה לעיתים לא קיים ב־leadimages,
 * אבל קיצור (למשל לפני " HOTEL AND SUITES") כן.
 * @param {unknown} rawName
 * @returns {string[]}
 */
export function expandHotelNameLookupCandidates(rawName) {
  const base = normalizeHotelNameForLeadImageLookup(rawName);
  if (!base) return [];

  const out = [];
  const add = (s) => {
    const n = normalizeHotelNameForLeadImageLookup(s);
    if (n && !out.includes(n)) out.push(n);
  };

  add(base);
  const lower = base.toLowerCase();
  const suffixes = [
    " hotel and suites",
    " hotel & suites",
    " hotel and suite",
    " hotels and suites",
    " hotel",
  ];
  for (const suf of suffixes) {
    if (lower.endsWith(suf)) {
      add(base.slice(0, base.length - suf.length).trim());
    }
  }

  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length >= 4) {
    add(parts.slice(0, 3).join(" "));
  }
  if (parts.length >= 3) {
    add(parts.slice(0, 2).join(" "));
  }

  return out;
}

/**
 * @param {number | null} hid
 * @param {string | null} nameForBody
 * @param {AbortSignal | undefined} signal
 */
async function singleGetImageRequest(hid, nameForBody, signal) {
  if (hid == null && !nameForBody) {
    return { url: null, source: "no_hotel_id_or_name" };
  }

  const key = cacheKeyForLeadImageLookup(hid, nameForBody || "");
  const now = Date.now();
  const hit = memoryCache.get(key);
  if (hit && now - hit.t < (hit.fail ? TTL_FAIL_MS : TTL_OK_MS)) {
    return { url: hit.url, source: hit.url ? "cache_hit" : "cache_negative" };
  }

  const existing = inflight.get(key);
  if (existing) return existing;

  const payload = {};
  if (hid != null) payload.hotelID = hid;
  if (nameForBody) payload.hotelName = nameForBody;

  const p = (async () => {
    try {
      const res = await axios.post(`${ApiReqBaseUrl}/getImage`, payload, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
        validateStatus: () => true,
        signal,
      });
      const url = absolutizeHotelImageUrlForDisplay(res.data?.image);
      if (url) {
        memoryCache.set(key, { url, t: Date.now(), fail: false });
        const src =
          hid != null ? "getImage" : nameForBody ? "getImage_by_hotel_name" : "getImage";
        return { url, source: src };
      }
      memoryCache.set(key, { url: null, t: Date.now(), fail: true });
      return { url: null, source: "getImage_empty" };
    } catch {
      memoryCache.set(key, { url: null, t: Date.now(), fail: true });
      return { url: null, source: "getImage_error" };
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, p);
  return p;
}

/**
 * @param {{ hotelId?: unknown, hotelName?: unknown, signal?: AbortSignal }} params
 * @returns {Promise<{ url: string | null, source: string }>}
 */
export async function getHotelImageForOrderDisplay(params = {}) {
  const hid = sanitizeNumericHotelIdForImageApi(params.hotelId);
  const primaryName = normalizeHotelNameForLeadImageLookup(params.hotelName);

  if (hid == null && !primaryName) {
    return { url: null, source: "no_hotel_id_or_name" };
  }

  if (hid != null) {
    return singleGetImageRequest(hid, primaryName, params.signal);
  }

  const candidates = expandHotelNameLookupCandidates(params.hotelName);
  let last = /** @type {{ url: string | null, source: string }} */ ({
    url: null,
    source: "getImage_empty",
  });

  for (let i = 0; i < candidates.length; i++) {
    const name = candidates[i];
    last = await singleGetImageRequest(null, name, params.signal);
    if (last.url) {
      return i === 0
        ? last
        : { ...last, source: `${last.source}_nameFallback` };
    }
  }

  return last;
}
